import { CHORDS, noteFreq } from '@gc/shared';

/**
 * Звук гитары синтезируется прямо в браузере алгоритмом Карплуса — Стронга:
 * короткий шум прогоняется по кольцевой линии задержки длиной в период ноты,
 * на каждом шаге усредняются соседние отсчёты — получается затухающая
 * струна. Так Mini App не тянет за собой ни одного аудиофайла: это важнее,
 * чем идеальный тембр, — приложение открывают с телефона по мобильной сети.
 */

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
const cache = new Map<number, AudioBuffer>();

type WebkitWindow = Window & { webkitAudioContext?: typeof AudioContext };

/**
 * Контекст создаётся лениво: браузеры на телефонах разрешают звук только
 * в ответ на действие пользователя, поэтому первый вызов всегда из обработчика.
 */
export function audio(): AudioContext {
  if (!ctx) {
    const Ctor = window.AudioContext ?? (window as WebkitWindow).webkitAudioContext;
    if (!Ctor) throw new Error('Браузер не умеет воспроизводить звук');
    ctx = new Ctor();
    master = ctx.createGain();
    master.gain.value = 0.9;
    master.connect(ctx.destination);
  }
  if (ctx.state === 'suspended') void ctx.resume();
  return ctx;
}

function buffer(freq: number): AudioBuffer {
  const key = Math.round(freq * 10);
  const hit = cache.get(key);
  if (hit) return hit;

  const c = audio();
  const sr = c.sampleRate;
  const seconds = 2.4;
  const total = Math.floor(sr * seconds);
  const buf = c.createBuffer(1, total, sr);
  const out = buf.getChannelData(0);

  const period = Math.max(2, Math.round(sr / freq));
  const line = new Float32Array(period);
  for (let i = 0; i < period; i++) {
    // Затухающий по длине шум звучит мягче ровного белого — ближе к щипку.
    line[i] = (Math.random() * 2 - 1) * (1 - (i / period) * 0.35);
  }

  // Толстые струны тянутся дольше тонких, поэтому затухание зависит от частоты.
  const damp = 0.9965 - Math.min(0.0025, freq / 260000);
  let idx = 0;
  for (let i = 0; i < total; i++) {
    const cur = line[idx]!;
    const next = line[(idx + 1) % period]!;
    out[i] = cur;
    line[idx] = ((cur + next) / 2) * damp;
    idx = (idx + 1) % period;
  }

  // Плавный хвост: обрыв на последнем отсчёте слышен как щелчок.
  const tail = Math.floor(total * 0.12);
  for (let i = total - tail; i < total; i++) out[i] = out[i]! * ((total - i) / tail);

  cache.set(key, buf);
  return buf;
}

export function pluck(stringIndex: number, fret: number, when?: number, gain = 0.5): void {
  const c = audio();
  const src = c.createBufferSource();
  src.buffer = buffer(noteFreq(stringIndex, fret));
  const g = c.createGain();
  // Тонкие струны в реальной гитаре звучат ярче — чуть добавляем им громкости.
  g.gain.value = gain * (0.75 + stringIndex * 0.05);
  src.connect(g);
  g.connect(master!);
  src.start(when ?? c.currentTime);
}

/** Задержка между струнами внутри одного удара — то, что делает бой боем. */
const SPREAD = 0.026;

export function strum(chord: string, up = false, when?: number): void {
  const shape = CHORDS[chord];
  if (!shape) return;
  const c = audio();
  const t = when ?? c.currentTime;

  const order = [0, 1, 2, 3, 4, 5];
  const seq = up ? [...order].reverse() : order;
  let played = 0;
  for (const s of seq) {
    const fret = shape.frets[s]!;
    if (fret < 0) continue; // заглушённая струна
    pluck(s, fret, t + played * SPREAD, up ? 0.36 : 0.46);
    played++;
  }
}

/** Щелчок метронома. Сильная доля выше и громче — её слышно в бою. */
export function click(when: number, accent = false): void {
  const c = audio();
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = 'square';
  osc.frequency.value = accent ? 1600 : 1050;
  g.gain.setValueAtTime(0.0001, when);
  g.gain.exponentialRampToValueAtTime(accent ? 0.22 : 0.13, when + 0.002);
  g.gain.exponentialRampToValueAtTime(0.0001, when + 0.05);
  osc.connect(g);
  g.connect(master!);
  osc.start(when);
  osc.stop(when + 0.07);
}
